import { errorOut } from '../services/datadog'
import type { AppStore } from '../store'

import { journeyIdFromUrl } from './journeyIdFromUrl'
import { readMarker } from './marker'

type AppState = ReturnType<AppStore['getState']>

type Loader = () => Promise<{ activateOffline(): Promise<unknown> }>

type Options = {
  href?: string
  storage?: Pick<Storage, 'getItem'>
  store?: AppStore
  load?: Loader
}

/** Upper bound on how long the first render waits for a marker-path activation. */
const MARKER_BOOT_TIMEOUT_MS = 3_000

/** `localStorage` itself throws (SecurityError) in storage-blocked embeds. */
const safeLocalStorage = (): Pick<Storage, 'getItem'> | null => {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

const fail = (message: string) => (originalError: unknown) => {
  const error = new Error(message)

  error.cause = originalError
  errorOut(error)
}

/**
 * Decides whether the offline add-on must be loaded, and when.
 *
 * - Journey prepared on this device (marker): load BEFORE the first request,
 *   otherwise the config fetch fails offline before any interceptor exists.
 * - Otherwise: wait for the config, and load if the org flag is on AND the
 *   served revision opted in (`settings.offline.enabled`, see `gate.ts`).
 *
 * Never throws: a broken add-on must leave the journey app as it is today.
 *
 * This module is in the entry chunk, so the store and its selectors are only
 * imported dynamically (they live in the AppShell chunks; a static import here
 * would more than double the entry bundle for every journey).
 */
export const bootstrapOffline = async ({
  href = window.location.href,
  storage,
  store,
  load = () => import('./index')
}: Options = {}): Promise<void> => {
  const journeyId = journeyIdFromUrl(href)

  if (!journeyId) return

  const activate = async () => {
    try {
      const addon = await load()

      await addon.activateOffline()
    } catch (originalError) {
      fail('Offline add-on failed to load')(originalError)
    }
  }

  const source = storage ?? safeLocalStorage()
  const marker = source ? readMarker(source) : {}

  if (marker[journeyId]) {
    let timer: ReturnType<typeof setTimeout> | undefined
    const timedOut = new Promise<'timeout'>((resolve) => {
      timer = setTimeout(() => resolve('timeout'), MARKER_BOOT_TIMEOUT_MS)
    })
    // Activation keeps going in the background if it loses the race.
    const winner = await Promise.race([
      activate().then(() => 'activated' as const),
      timedOut
    ])

    clearTimeout(timer)

    if (winner === 'timeout') {
      errorOut(
        new Error(
          `Offline add-on: activation took over ${MARKER_BOOT_TIMEOUT_MS}ms, rendering without it`
        )
      )
    }

    return
  }

  // Not awaited: the render must not wait for the store chunk.
  void Promise.all([
    store ?? import('../store').then((module) => module.appStore),
    import('../store/selectors/config'),
    import('./gate')
  ])
    .then(([target, { selectHydratedJourneyId }, { isOfflineOn }]) => {
      const onState = (state: AppState) => {
        if (!selectHydratedJourneyId(state)) return

        unsubscribe()

        if (isOfflineOn(state)) void activate()
      }
      const unsubscribe = target.subscribe(onState)

      // Hydration may have happened while the store chunk was loading.
      onState(target.getState())
    })
    .catch(fail('Offline add-on: store unavailable'))
}
